import type { Hit as AlgoliaHit } from 'algoliasearch';
import { useTranslations } from 'next-intl';

import { NODE_BUNDLE } from '@commons/pagebuilder/nodes';

/**
 * Type definition for the supported hit types from Drupal.
 */

export type HitType =
  | NODE_BUNDLE.Page
  | NODE_BUNDLE.CampaignPage
  | NODE_BUNDLE.Article
  | NODE_BUNDLE.PressRelease
  | NODE_BUNDLE.CaseStudy
  | NODE_BUNDLE.Event
  | NODE_BUNDLE.Podcast
  | NODE_BUNDLE.Webinar
  | NODE_BUNDLE.WhitePaper;

/**
 * Type definition for a search result hit from Algolia.
 */

export type Hit = AlgoliaHit<{
  type: HitType;
  title: string;
  alias?: string;
  image?: string;
  image_focal_point?: string;
  date?: number;
  custom_date?: string;
  button_label?: string;
  format?: string;
  topics?: string | string[];
}>;

/**
 * A constant containing the default hit attributes to retrieve from Algolia.
 */

export const ATTRIBUTES_TO_RETRIEVE = [
  'type',
  'title',
  'alias',
  'image',
  'image_focal_point',
  'date',
  'custom_date',
  'button_label',
  'format',
  'topics',
];

/**
 * Custom hook that returns a `t` function for translating the hit type labels.
 *
 * This is necessary because the Drupal machine names do not match the frontend translations keys.
 * We also need to camelCase the string, which breaks next-intl's type inference.
 *
 * @returns A `t` function that translates the hit type labels.
 */

export const useHitTypeTranslation = () => {
  const t = useTranslations('nodes');

  const translateType = (type: HitType, message: 'label' | 'pluralLabel') => {
    switch (type) {
      case NODE_BUNDLE.Page:
        return t(`page.${message}`);
      case NODE_BUNDLE.CampaignPage:
        return t(`campaign.${message}`);
      case NODE_BUNDLE.PressRelease:
        return t(`pressRelease.${message}`);
      case NODE_BUNDLE.CaseStudy:
        return t(`caseStudy.${message}`);
      case NODE_BUNDLE.WhitePaper:
        return t(`whitePaper.${message}`);
      default:
        return t(`${type}.${message}`);
    }
  };

  return translateType;
};
